import React, { useState } from 'react';
import { Drawer, Table, Button, message, Popconfirm } from 'antd';
import { useQuery } from '@apollo/client';
import { t } from '../../../../i18n';
import {
    GET_BLACKLISTED_DELIVERY_DRIVERS,
    GetBlacklistedDeliveryDriversResponse,
    GetBlacklistedDeliveryDriversVariables,
    BlacklistedDeliveryDriverType,
} from '../graphql/queries/getBlacklistedDrivers';
import { setDriverBlacklist } from '../graphql/mutations/setDriverBlacklist';
import { showError } from 'utils/exceptionUtils';
import { formatTimestampToDateTime } from 'utils/dateTimeUtils';

const BLACKLIST_ERROR_I18N: Record<string, string> = {
    BLACKLIST_ERR_NO_PERMISSION: 'delivery.personnel.message.blacklistErrNoPermission',
    BLACKLIST_ERR_PHONE_LIST_EMPTY: 'delivery.personnel.message.blacklistErrPhoneListEmpty',
    BLACKLIST_ERR_NO_VALID_PHONES: 'delivery.personnel.message.blacklistErrNoValidPhones',
    BLACKLIST_ERR_GENERAL: 'delivery.personnel.message.blacklistErrGeneral',
};

interface DriverBlacklistListModalProps {
    visible: boolean;
    onClose: () => void;
}

const DriverBlacklistListModal: React.FC<DriverBlacklistListModalProps> = ({ visible, onClose }) => {
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
    const { data, loading, refetch } = useQuery<GetBlacklistedDeliveryDriversResponse, GetBlacklistedDeliveryDriversVariables>(
        GET_BLACKLISTED_DELIVERY_DRIVERS,
        {
            variables: {
                pageRequest: {
                    page: pagination.current,
                    size: pagination.pageSize,
                },
            },
            skip: !visible,
            fetchPolicy: 'cache-and-network',
        }
    );

    const pageData = data?.getBlacklistedDeliveryDrivers;

    const handleUnban = async (record: BlacklistedDeliveryDriverType) => {
        if (!record.phone) {
            message.warning(t('delivery.personnel.message.unbanNoPhone'));
            return;
        }
        try {
            const result = await setDriverBlacklist({
                phones: [record.phone],
                isBlacklist: false,
                banReason: 0,
                banType: 0,
            });
            if (result.success) {
                message.success(t('delivery.personnel.message.unbanSuccess'));
                refetch();
            } else {
                message.error(t(BLACKLIST_ERROR_I18N[result.message] ?? 'delivery.personnel.message.unbanFailed'));
            }
        } catch (error: any) {
            showError(error);
        }
    };

    const columns = [
        {
            title: t('delivery.personnel.label.driverPhone'),
            dataIndex: 'phone',
            key: 'phone',
            width: 130,
            render: (v: number | null) => v || '-',
        },
        {
            title: t('delivery.personnel.label.region'),
            dataIndex: 'regionName',
            key: 'regionName',
            width: 100,
            render: (v: string | null) => v || '-',
        },
        {
            title: t('delivery.personnel.label.warehouse'),
            dataIndex: 'warehouseName',
            key: 'warehouseName',
            width: 120,
            render: (v: string | null) => v || '-',
        },
        {
            title: t('delivery.personnel.label.dsp'),
            dataIndex: 'dspName',
            key: 'dspName',
            width: 120,
            render: (v: string | null) => v || '-',
        },
        {
            title: t('delivery.personnel.label.banReason'),
            dataIndex: 'banReason',
            key: 'banReason',
            width: 140,
            render: (v: number | null) => {
                if (!v) return '-';
                const map: Record<number, string> = {
                    1: t('delivery.personnel.label.banReason1'),
                    2: t('delivery.personnel.label.banReason2'),
                    3: t('delivery.personnel.label.banReason3'),
                    4: t('delivery.personnel.label.banReason4'),
                    5: t('delivery.personnel.label.banReason5'),
                };
                return map[v] ?? '-';
            },
        },
        {
            title: t('delivery.personnel.label.banPeriod'),
            dataIndex: 'banPeriod',
            key: 'banPeriod',
            width: 100,
            render: (v: number | null) => {
                if (!v) return t('delivery.personnel.label.permanentBan');
                const map: Record<number, string> = {
                    1: t('delivery.personnel.label.banPeriod1'),
                    2: t('delivery.personnel.label.banPeriod2'),
                    3: t('delivery.personnel.label.banPeriod3'),
                    4: t('delivery.personnel.label.banPeriod4'),
                    5: t('delivery.personnel.label.banPeriod5'),
                };
                return map[v] ?? t('delivery.personnel.label.permanentBan');
            },
        },
        {
            title: t('delivery.personnel.label.banTime'),
            dataIndex: 'banTime',
            key: 'banTime',
            width: 160,
            render: (v: string | null) => {
                if (!v) return '-';
                const ts = Number(v);
                return isNaN(ts) ? v : formatTimestampToDateTime(ts);
            },
        },
        {
            title: t('delivery.personnel.label.banOperator'),
            dataIndex: 'banOperator',
            key: 'banOperator',
            width: 100,
            render: (v: string | null) => v || '-',
        },
        {
            title: t('common.action'),
            key: 'action',
            fixed: 'right' as const,
            render: (_: any, record: BlacklistedDeliveryDriverType) => (
                <Popconfirm
                    title={t('delivery.personnel.message.unbanConfirm')}
                    onConfirm={() => handleUnban(record)}
                    okText={t('components.button.confirm')}
                    cancelText={t('components.button.cancel')}
                >
                    <Button
                        type="link"
                        size="small"
                    >
                        {t('delivery.personnel.label.unban')}
                    </Button>
                </Popconfirm>
            ),
        },
    ];

    return (
        <Drawer
            title={
                <div className="flex items-center">
                    <div className="h-6 w-1 bg-blue-600 mr-3"></div>
                    <span className="text-xl font-medium">{t('delivery.personnel.label.driverBlacklist')}</span>
                </div>
            }
            open={visible}
            onClose={onClose}
            width={1000}
            destroyOnClose
        >
            <div className="text-red-500 text-sm mb-3 flex items-center gap-1">
                <span className="inline-flex items-center justify-center w-4 h-4 rounded-full border border-red-500 text-xs font-bold shrink-0">!</span>
                {t('delivery.personnel.message.blacklistWarning')}
            </div>
            <Table
                columns={columns}
                dataSource={pageData?.content || []}
                rowKey="accountId"
                loading={loading}
                scroll={{ x: 'max-content' }}
                pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pageData?.totalElements || 0,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total) => t('table.pagination.totalItems', { total }),
                    onChange: (page, size) => setPagination({ current: page, pageSize: size }),
                }}
            />
        </Drawer>
    );
};

export default DriverBlacklistListModal;
